import { Component, type ErrorInfo, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/shared/components/ErrorState'
import { SystemStateIllustration } from '@/shared/components/illustrations/SystemStateIllustration'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'

interface AppErrorBoundaryProps {
  children: ReactNode
}

interface AppErrorBoundaryState {
  hasError: boolean
  incidentId: string | null
}

function AppErrorFallback({ onRetry, incidentId }: { onRetry: () => void; incidentId: string | null }) {
  const { t } = useTranslation()
  useDocumentTitle(t('ui.global_error.page_title'))

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-3xl items-center px-4 py-10">
      <ErrorState
        title={t('ui.global_error.title')}
        description={t('ui.global_error.description')}
        retryLabel={t('ui.global_error.retry')}
        onRetry={onRetry}
        className="w-full"
      >
        <SystemStateIllustration variant="error" className="mt-2 mb-4" />
        {incidentId ? (
          <p className="mt-3 font-mono text-xs text-muted-foreground">
            {t('ui.global_error.incident', { id: incidentId })}
          </p>
        ) : null}
      </ErrorState>
    </main>
  )
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  public state: AppErrorBoundaryState = { hasError: false, incidentId: null }

  public static getDerivedStateFromError(): Partial<AppErrorBoundaryState> {
    return { hasError: true }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const incidentId = Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })
    this.setState({ incidentId })
  }

  public render() {
    if (this.state.hasError) {
      return (
        <AppErrorFallback
          incidentId={this.state.incidentId}
          onRetry={() => this.setState({ hasError: false, incidentId: null })}
        />
      )
    }

    return this.props.children
  }
}
